const { useState } = React;
const t = window.NinjaPearI18n?.t || ((key) => key);

// Configuration is passed via window.REGISTRATION_CONFIG from the HTML template
const config = window.REGISTRATION_CONFIG || {
  isFromInvitation: false,
  initialEmail: '',
  recaptchaSiteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
  termsOfUseUrl: '/terms',
  currentLocale: 'en',
  registerActionUrl: '/auth/register'
};

function RegistrationForm() {
  const isFromInvitation = config.isFromInvitation;
  const [formData, setFormData] = useState({
    email: config.initialEmail,
    password: "",
    confirmPassword: "",
    referralSource: isFromInvitation ? "invitation" : "",
    termsAgree: false,
  });

  const [domainValidation, setDomainValidation] = useState({
    isValidating: false,
    isValid: null,
    reason: null,
    domain: null,
    trialEligible: null,
    eligibilityReason: null,
    skipBlockBucket: null,
  });

  const [topUpAcknowledged, setTopUpAcknowledged] = useState(false);
  const [freeEmailProviders, setFreeEmailProviders] = useState(null);

  // Load the free-email provider list once so we can render the skip-block
  // alert instantly (the backend /auth/api/validate-domain call remains the
  // source of truth and overwrites this optimistic state when it returns).
  React.useEffect(() => {
    let cancelled = false;
    fetch('/assets/txt/free_email_providers.txt')
      .then((resp) => (resp.ok ? resp.text() : null))
      .then((text) => {
        if (cancelled || text == null) return;
        const providers = new Set(
          text
            .split('\n')
            .map((line) => line.trim().toLowerCase())
            .filter((line) => line && !line.startsWith('#'))
        );
        setFreeEmailProviders(providers);
      })
      .catch(() => {
        // Fail open - backend still classifies.
      });
    return () => { cancelled = true; };
  }, []);

  const [captchaState, setCaptchaState] = useState({
    isLoading: true,
    isSolved: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const domainValidationTimeoutRef = React.useRef(null);
  const validationRequestSequenceRef = React.useRef(0);
  const [registrationAttemptId, setRegistrationAttemptId] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Validate domain when email changes (skip if from invitation)
    if (name === 'email' && !isFromInvitation) {
      validateDomainFromEmail(value);
    }
  };

  const clearDomainValidation = () => {
    setRegistrationAttemptId(null);
    setDomainValidation({
      isValidating: false,
      isValid: null,
      reason: null,
      domain: null,
      trialEligible: null,
      eligibilityReason: null,
      skipBlockBucket: null,
    });
    setTopUpAcknowledged(false);
  };

  const handleRetryWithWorkEmail = () => {
    // Primary CTA on the skip-block alert: clear the email + the alert so the
    // user can enter a work email. Cancel any in-flight debounced backend call.
    validationRequestSequenceRef.current += 1;
    if (domainValidationTimeoutRef.current) {
      clearTimeout(domainValidationTimeoutRef.current);
      domainValidationTimeoutRef.current = null;
    }
    setFormData((prev) => ({ ...prev, email: '' }));
    clearDomainValidation();
    const emailField = document.getElementById('email');
    if (emailField) {
      emailField.focus();
    }
  };

  const validateDomainFromEmail = (email) => {
    const validationRequestSequence = validationRequestSequenceRef.current + 1;
    validationRequestSequenceRef.current = validationRequestSequence;

    if (domainValidationTimeoutRef.current) {
      clearTimeout(domainValidationTimeoutRef.current);
      domainValidationTimeoutRef.current = null;
    }

    // Clear previous validation state
    clearDomainValidation();

    if (!email || !email.includes('@')) {
      return;
    }

    const domain = email.split('@')[1];
    if (!domain || domain.length < 3) {
      return;
    }

    const normalizedDomain = domain.toLowerCase();

    // Instant client-side skip-block signal for known free providers so the
    // alert renders without waiting on the 1.5s debounce + backend round-trip.
    // Backend still re-classifies (disposable / .edu) and overwrites below.
    if (freeEmailProviders && freeEmailProviders.has(normalizedDomain)) {
      setDomainValidation({
        isValidating: false,
        isValid: false,
        reason: 'Free email provider not accepted',
        domain: domain,
        trialEligible: false,
        eligibilityReason: 'skip_block',
        skipBlockBucket: 'free',
      });
    } else {
      // Set validating state
      setDomainValidation(prev => ({
        ...prev,
        isValidating: true,
        domain: domain
      }));
    }

    domainValidationTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch('/auth/api/validate-domain', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: email, domain: domain })
        });

        const result = await response.json();

        if (validationRequestSequence !== validationRequestSequenceRef.current) {
          return;
        }

        if (response.ok) {
          window.umami?.track('reg-domain-validated', {
            valid: result.accessible,
            domain: domain,
            skip_block: result.skip_block_bucket || 'none',
          });
          setDomainValidation({
            isValidating: false,
            isValid: result.accessible,
            reason: result.reason,
            domain: domain,
            trialEligible: result.trial_eligible,
            eligibilityReason: result.eligibility_reason,
            skipBlockBucket: result.skip_block_bucket || null,
          });
          setRegistrationAttemptId(result.registration_attempt_id || null);
        } else {
          setDomainValidation({
            isValidating: false,
            isValid: false,
            reason: result.error || 'Failed to validate domain',
            domain: domain,
            trialEligible: false,
            eligibilityReason: 'validation_error',
            skipBlockBucket: null,
          });
          setRegistrationAttemptId(null);
        }
      } catch (error) {
        if (validationRequestSequence !== validationRequestSequenceRef.current) {
          return;
        }
        console.warn('Domain validation error:', error);
        setDomainValidation({
          isValidating: false,
          isValid: null,
          reason: 'Unable to validate domain',
          domain: domain,
          trialEligible: null,
          eligibilityReason: null,
          skipBlockBucket: null,
        });
        setRegistrationAttemptId(null);
      }
    }, 1500); // 1.5 second debounce
  };

  React.useEffect(() => () => {
    validationRequestSequenceRef.current += 1;
    if (domainValidationTimeoutRef.current) {
      clearTimeout(domainValidationTimeoutRef.current);
      domainValidationTimeoutRef.current = null;
    }
  }, []);

  // Validate, then submit natively. Every signup lands on the developer (API)
  // track; the server assigns it.
  const submitRegistration = (form) => {
    if (isSubmitDisabled) {
      return;
    }

    // Run Parsley validation
    const parsley = $(form).parsley();
    parsley.validate();

    if (!parsley.isValid()) {
      return;
    }

    setIsSubmitting(true);
    window.umami?.track('reg-submitted', { track: 'api' });
    window.requestAnimationFrame(() => {
      form.submit();
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitRegistration(e.target);
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter' || e.target.tagName === 'TEXTAREA') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const form = document.getElementById('registration-form');
    if (form) {
      submitRegistration(form);
    }
  };

  const clearRecaptchaError = () => {
    const errorElement = document.querySelector('.recaptcha-error');
    if (errorElement) {
      errorElement.remove();
    }
  };

  // Initialize Parsley after component mounts
  React.useEffect(() => {
    const form = document.getElementById('registration-form');
    if (form) {
      $(form).parsley({
        errorClass: 'is-invalid',
        successClass: 'is-valid',
        errorsWrapper: '<div class="invalid-feedback d-block"></div>',
        errorTemplate: '<span></span>'
      });
    }
  }, []);

  // Re-validate email field when domain validation results change
  React.useEffect(() => {
    if (domainValidation.domain && domainValidation.isValid !== null) {
      const emailField = document.getElementById('email');
      if (emailField && $(emailField).parsley()) {
        $(emailField).parsley().validate();
      }
    }
  }, [domainValidation]);

  // Detect and set user timezone on component mount
  React.useEffect(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneInput = document.getElementById('userTimezone');
      if (timezoneInput && timezone) {
        timezoneInput.value = timezone;
        console.log('Detected timezone:', timezone);
      }
    } catch (error) {
      console.warn('Failed to detect timezone:', error);
      // Fallback to UTC if detection fails
      const timezoneInput = document.getElementById('userTimezone');
      if (timezoneInput) {
        timezoneInput.value = 'UTC';
      }
    }
  }, []); // Run only once on mount

  // Validate domain on initial load if email is pre-filled
  React.useEffect(() => {
    if (formData.email) {
      validateDomainFromEmail(formData.email);
    }
  }, []); // Run only once on mount

  // Set up reCAPTCHA callbacks and render explicitly
  React.useEffect(() => {
    function renderCaptcha() {
      setCaptchaState(prev => ({ ...prev, isLoading: false }));

      const container = document.getElementById('captcha-container');
      if (container && typeof grecaptcha !== 'undefined') {
        grecaptcha.render('captcha-container', {
          'sitekey': config.recaptchaSiteKey,
          'callback': function(response) {
            setCaptchaState(prev => ({ ...prev, isSolved: true }));
            clearRecaptchaError();
          },
          'expired-callback': function() {
            setCaptchaState(prev => ({ ...prev, isSolved: false }));
          }
        });
      }
    }

    // Set up callback for when reCAPTCHA loads after React mounts
    window.onRecaptchaLoadReact = renderCaptcha;

    // If reCAPTCHA already loaded before React mounted, render now
    if (window.recaptchaReady) {
      renderCaptcha();
    }

    return () => {
      window.onRecaptchaLoadReact = null;
    };
  }, []);

  const isSubmitDisabled =
    !captchaState.isSolved ||
    isSubmitting ||
    (domainValidation.skipBlockBucket != null && !topUpAcknowledged);

  return (
    <form
      id="registration-form"
      className="mb-6"
      method="POST"
      action={config.registerActionUrl || "/auth/register"}
      onSubmit={handleSubmit}
      onKeyDown={handleFormKeyDown}
      data-parsley-validate
    >
      <input type="hidden" name="preferredLocale" value={config.currentLocale || "en"} />
      <input type="hidden" name="registrationAttemptId" value={registrationAttemptId || ""} />
      {/* Email */}
      <div className="form-group flex justify-center">
        <div className="w-full max-w-[304px]">
          <label className="form-label" htmlFor="email">
            {t("Work Email")}
          </label>
          <input
            type="email"
            className={`form-control w-full ${isFromInvitation ? 'bg-gray-100' : ''}`}
            id="email"
            name="email"
            placeholder="name@address.com"
            value={formData.email}
            onChange={handleInputChange}
            required
            readOnly={isFromInvitation}
            data-parsley-type="email"
            data-parsley-trigger="change"
          />
          {/* Invitation email notice */}
          {isFromInvitation && (
            <div className="text-blue-600 text-sm mt-1">
              This email was provided from your team invitation
            </div>
          )}
          {/* Domain validation feedback */}
          {domainValidation.isValidating && (
            <div className="text-zinc-600 text-sm mt-1 flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-zinc-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking if {domainValidation.domain} has an accessible website...
            </div>
          )}
          {domainValidation.skipBlockBucket == null && domainValidation.isValid === true && domainValidation.trialEligible === true && (
            <div className="text-green-600 text-sm mt-1 flex items-center">
              <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Website accessible at {domainValidation.domain}
            </div>
          )}
          {/* Warning: Domain accessible but NOT trial-eligible (parking page detected) */}
          {domainValidation.skipBlockBucket == null && domainValidation.isValid === true && domainValidation.trialEligible === false && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-amber-800 text-sm font-medium">
                    This email domain is not eligible for trial credits
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    We detected that {domainValidation.domain} does not have a legitimate business website.
                    To receive free trial credits, please use an email from a domain with an active company website.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Warning: Email triggers paid skip-block (free / edu / disposable) */}
          {domainValidation.skipBlockBucket != null && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mt-2">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-amber-800 text-sm font-medium">
                    NinjaPear is intended for business use-cases.
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    We do not accept free emails. Register with a work email. Alternatively,
                    you can make a top-up to demonstrate that you intend to use NinjaPear
                    for business reasons.
                  </p>
                  <div className="mt-3 flex flex-col gap-2">
                    <button
                      type="button"
                      className="btn btn-primary w-full"
                      onClick={handleRetryWithWorkEmail}
                    >
                      {t("I will retry with a Work Email")}
                    </button>
                    <button
                      type="button"
                      className={`btn w-full text-sm text-left ${topUpAcknowledged ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setTopUpAcknowledged(true)}
                      aria-pressed={topUpAcknowledged}
                    >
                      {topUpAcknowledged ? '✓ ' : ''}
                      I do not have a work email and I will make payment to open an account.
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Warning: Domain not accessible */}
      {domainValidation.isValid === false && domainValidation.skipBlockBucket == null && (
        <div className="form-group flex justify-center">
          <div className="w-full max-w-[304px]">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-amber-800 text-sm font-medium">
                    This email domain is not eligible for trial credits
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    We could not find an accessible website at {domainValidation.domain}.
                    To receive free trial credits, please use an email from a domain with an active company website.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password */}
      <div className="form-group flex justify-center">
        <div className="w-full max-w-[304px]">
          <label className="form-label" htmlFor="password">
            {t("Password")}
          </label>
          <input
            type="password"
            className="form-control w-full"
            id="password"
            name="password"
            placeholder="Enter your password"
            value={formData.password}
            onChange={handleInputChange}
            required
            data-parsley-trigger="change"
          />
        </div>
      </div>

      {/* Re-type Password */}
      <div className="form-group flex justify-center">
        <div className="w-full max-w-[304px]">
          <label className="form-label" htmlFor="confirmPassword">
            {t("Re-type Password")}
          </label>
          <input
            type="password"
            className="form-control w-full"
            id="confirmPassword"
            name="confirmPassword"
            placeholder="Re-enter your password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            data-parsley-equalto="#password"
            data-parsley-trigger="change"
          />
        </div>
      </div>

      {/* How did you find out about NinjaPear */}
      <div className="form-group flex justify-center">
        <div className="w-full max-w-[304px]">
          <label className="form-label" htmlFor="referralSource">
            {t("How did you find out about NinjaPear?")}
          </label>
          <select
            className="form-control w-full"
            id="referralSource"
            name="referralSource"
            value={formData.referralSource}
            onChange={handleInputChange}
            required
            data-parsley-trigger="change"
          >
            <option value="">{t("Select an option")}</option>
            <option value="invitation">{t("I was invited")}</option>
            <option value="search-engine">
              {t("Search Engine (Google, Bing, etc.)")}
            </option>
            <option value="social-media">{t("Social Media")}</option>
            <option value="word-of-mouth">{t("Word of Mouth")}</option>
            <option value="advertisement">{t("Advertisement")}</option>
            <option value="blog-article">{t("Blog/Article")}</option>
            <option value="looking-for-proxycurl">
              {t("I am looking for Proxycurl")}
            </option>
            <option value="chatgpt">ChatGPT</option>
            <option value="claude">
              {t("Claude Code (or other Claude products)")}
            </option>
            <option value="ai-agents">
              {t("AI Agents (Not ChatGPT/Claude)")}
            </option>
            <option value="other">{t("Other")}</option>
          </select>
        </div>
      </div>

      {/* Terms of Use Checkbox */}
      <div className="form-group flex justify-center">
        <div className="w-full max-w-[304px]">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="termsAgree"
              name="termsAgree"
              checked={formData.termsAgree}
              onChange={handleInputChange}
              required
              data-parsley-trigger="change"
            />
            <label className="form-check-label" htmlFor="termsAgree">
              {t("I agree to the")}{" "}
              <a href={config.termsOfUseUrl} className="underline">
                {t("Terms Of Use")}
              </a>
            </label>
          </div>
        </div>
      </div>

      {/* Captcha */}
      <div className="my-4 flex justify-center">
        <div className="w-full max-w-[304px]">
          {/* Loading spinner */}
          {captchaState.isLoading && (
            <div className="flex items-center justify-center h-[78px]">
              <svg className="animate-spin h-6 w-6 text-zinc-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="ml-2 text-sm text-zinc-500">{t("Loading captcha...")}</span>
            </div>
          )}
          {/* Captcha container - always in DOM, hidden while loading */}
          <div id="captcha-container" className={captchaState.isLoading ? 'hidden' : ''}></div>
        </div>
      </div>

      {/* Hidden timezone input */}
      <input type="hidden" name="userTimezone" id="userTimezone" />

      {/* Submit validates, then posts the form. */}
      <div className="flex justify-center">
        <div className="relative w-full max-w-[304px]">
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className={`btn btn-primary w-full ${isSubmitDisabled ? 'disabled' : ''}`}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t("Creating account...")}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {domainValidation.skipBlockBucket != null
                  ? t('Sign up & continue to payment')
                  : t('Sign up')}
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

// Self-initialize the form
const container = document.getElementById("registration-form-container");
if (container) {
  const root = ReactDOM.createRoot(container);
  root.render(<RegistrationForm />);
}
